/** Колокольчик в шапке: bell-icon, бейдж со счётчиком, открывает Dropdown.
 *
 *  Использует chrome-токены (живёт в тёмном топбаре).
 *  Состояния (см. фрейм «11 Notifications Dropdown» в .pen):
 *  * default — иконка приглушена, бейджа нет.
 *  * unread — bell, бейдж --danger со счётчиком (>99 → «99+»).
 *  * no-connection — bell-off, цвет --warning, серая точка-индикатор.
 */
import { type Component, Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { Bell, BellOff } from "lucide-solid";
import { useI18n } from "~/i18n";
import { useNotifications } from "~/contexts/NotificationsProvider";
import { NotificationDropdown } from "~/components/layout/NotificationDropdown";
import { NotificationDetailModal } from "~/components/layout/NotificationDetailModal";
import type { Notification } from "~/lib/notifications";

export const NotificationBell: Component = () => {
  const { t } = useI18n();
  const { summary, pollingActive, scope } = useNotifications();
  const [open, setOpen] = createSignal(false);
  const [detail, setDetail] = createSignal<Notification | null>(null);
  let rootRef: HTMLDivElement | undefined;

  const onDocClick = (e: MouseEvent) => {
    if (rootRef && !rootRef.contains(e.target as Node)) setOpen(false);
  };
  const onEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  };
  onMount(() => {
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    onCleanup(() => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    });
  });

  const unread = createMemo(() => {
    const s = summary();
    if (!s) return 0;
    return scope() === "active" ? s.unread_active : s.unread_all;
  });
  // Offline-индикатор колокольчика — по реальному polling-fallback (дебаунс 5с),
  // а не по сырому wsState, чтобы короткие реконнекты не дёргали иконку.
  const offline = createMemo(() => pollingActive());
  const badgeText = createMemo(() => {
    const n = unread();
    if (n <= 0) return null;
    return n > 99 ? "99+" : String(n);
  });

  return (
    <div ref={rootRef} class="relative">
      <button
        type="button"
        aria-label={t("notifications.bellAria")}
        onClick={() => setOpen(!open())}
        class="relative flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-chrome-hover"
        classList={{ "bg-chrome-hover": open() }}
      >
        <Show
          when={!offline()}
          fallback={<BellOff size={18} class="text-warning" stroke-width={1.8} />}
        >
          <Bell
            size={18}
            class={unread() > 0 ? "text-chrome-ink" : "text-chrome-ink-muted"}
            stroke-width={1.8}
          />
        </Show>

        {/* Badge с числом */}
        <Show when={badgeText() !== null}>
          <span
            class="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white ring-2 ring-chrome"
          >
            {badgeText()}
          </span>
        </Show>

        {/* Точка-индикатор offline без unread */}
        <Show when={offline() && badgeText() === null}>
          <span class="absolute right-1 top-1 h-2 w-2 rounded-full bg-warning ring-2 ring-chrome" />
        </Show>
      </button>

      <Show when={open()}>
        {/* Затеняющий фон. Цвет в формате rgba — токены `--ink/--foreground`
            хранятся как hex, поэтому Tailwind-модификатор `/40` к ним не
            применяется (Tailwind ждёт `R G B`-числа). Хардкодим напрямую,
            оттенок такой же, как у модалок (см. фрейм «06 Modals» в .pen). */}
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          class="fixed inset-0 z-40 bg-[rgba(15,23,42,0.55)]"
        />
        <NotificationDropdown
          onClose={() => setOpen(false)}
          onOpenDetail={(n) => {
            setDetail(n);
            setOpen(false);
          }}
        />
      </Show>

      {/* Модалка полной информации об уведомлении (по действию «Открыть»). */}
      <NotificationDetailModal notification={detail()} onClose={() => setDetail(null)} />
    </div>
  );
};
